import React, { useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import ObtieneDatosCookies from "../obtieneDatosCookiesUsuario";

const ImportarNotasMoodleSIGEDI = ({ instancia, cierro }) => {
  const Usuario = ObtieneDatosCookies();

  const [archivoNombre, setArchivoNombre] = useState("");
  const [filasExcel, setFilasExcel] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [bloques, setBloques] = useState([]);
  const [resultadoCruce, setResultadoCruce] = useState([]);
  const [cargando, setCargando] = useState(false);

  const obtenerUsuarioDesdeEmail = (email) => {
    const limpio = String(email || "").trim();
    return limpio.includes("@") ? limpio.split("@")[0].trim() : limpio;
  };

  const normalizarNumero = (valor) => {
    if (valor === null || valor === undefined || valor === "") return 0;
    return Number(String(valor).replace(",", ".")) || 0;
  };

  const obtenerColumnasPreguntas = (fila) => {
    return Object.keys(fila).filter((key) =>
      String(key).trim().startsWith("P.")
    );
  };

  const cargarMaterias = async () => {
    if (!instancia?.nombreCurso) {
      Swal.fire("Atención", "No se encontró el curso de la instancia.", "warning");
      return;
    }

    try {
      setCargando(true);

      const response = await axios.post(
        "https://wwwa.isepsantafe.net.ar/expedientes/calificaciones/devuelveMateriasPorCurso",
        { curso: instancia.nombreCurso }
      );

      let materiasObtenidas = response.data.materias || [];

      if (instancia.exclusivo) {
        try {
          const exclusivoJSON = JSON.parse(instancia.exclusivo);
          materiasObtenidas = materiasObtenidas.filter((m) => exclusivoJSON[m.id]);
        } catch (e) {
          console.error("Error parseando exclusivo:", e);
        }
      }

      setMaterias(materiasObtenidas);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudieron obtener las materias.", "error");
    } finally {
      setCargando(false);
    }
  };

  const leerExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setArchivoNombre(file.name);

      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const hoja = workbook.Sheets[workbook.SheetNames[0]];
      const filas = XLSX.utils.sheet_to_json(hoja, { defval: "" });

      if (!filas.length) {
        Swal.fire("Archivo vacío", "No se encontraron datos.", "warning");
        return;
      }

      const columnasPreguntas = obtenerColumnasPreguntas(filas[0]);
      const cantidadPreguntas = columnasPreguntas.length;

      if (cantidadPreguntas < 5 || cantidadPreguntas % 5 !== 0) {
        Swal.fire(
          "Cantidad de preguntas inválida",
          `El archivo tiene ${cantidadPreguntas} preguntas. Debe tener al menos 5 preguntas y ser múltiplo de 5.`,
          "error"
        );
        return;
      }

      const nuevosBloques = Array.from(
        { length: cantidadPreguntas / 5 },
        (_, index) => ({
          numero: index + 1,
          desde: index * 5 + 1,
          hasta: index * 5 + 5,
          idMateria: "",
        })
      );

      const procesadas = filas
        .map((fila) => {
          const dni = obtenerUsuarioDesdeEmail(
            fila["Dirección de correo electrónico"] ||
              fila["Email"] ||
              fila["Correo electrónico"] ||
              fila["Usuario"] ||
              fila["DNI"]
          );

          const respuestas = columnasPreguntas.map((key) => ({
            pregunta: key,
            valor: normalizarNumero(fila[key]),
          }));

          return {
            dni: String(dni).trim(),
            respuestas,
            filaOriginal: fila,
          };
        })
        .filter((f) => f.dni);

      setFilasExcel(procesadas);
      setBloques(nuevosBloques);
      setResultadoCruce([]);

      if (!procesadas.length) {
        Swal.fire("Atención", "No pude detectar DNI en el Excel.", "warning");
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo leer el Excel.", "error");
    }
  };

  const cambiarMateriaBloque = (numeroBloque, idMateria) => {
    setBloques((prev) =>
      prev.map((b) =>
        b.numero === numeroBloque ? { ...b, idMateria } : b
      )
    );
    setResultadoCruce([]);
  };

  const calcularNotaBloque = (fila, bloque) => {
    const desdeIndex = bloque.desde - 1;
    const hastaIndex = bloque.hasta;

    const respuestasBloque = fila.respuestas.slice(desdeIndex, hastaIndex);
    const correctas = respuestasBloque.filter((r) => r.valor > 0).length;

    return {
      correctas,
      nota: correctas * 2,
    };
  };

  const cruzarDatos = async () => {
    if (!instancia?.id) {
      Swal.fire("Atención", "No se recibió id de instancia.", "warning");
      return;
    }

    if (!filasExcel.length) {
      Swal.fire("Atención", "Primero cargá el Excel de Moodle.", "warning");
      return;
    }

    if (!materias.length) {
      Swal.fire("Atención", "Primero cargá las materias del curso.", "warning");
      return;
    }

    const bloquesSinMateria = bloques.filter((b) => !b.idMateria);

    if (bloquesSinMateria.length > 0) {
      Swal.fire(
        "Faltan materias",
        "Cada bloque de 5 preguntas debe tener una materia asociada.",
        "warning"
      );
      return;
    }

    try {
      setCargando(true);

      const materiasPorId = {};
      materias.forEach((m) => {
        materiasPorId[String(m.id)] = m;
      });

      const mapaAlumnosPorMateria = {};

      for (const bloque of bloques) {
        const materia = materiasPorId[String(bloque.idMateria)];
        const asignaciones = materia?.asignaciones || [];

        mapaAlumnosPorMateria[String(bloque.idMateria)] = {};

        for (const asignacion of asignaciones) {
          const resp = await axios.post(
            "https://wwwa.isepsantafe.net.ar/expedientes/calificaciones/devuelveAlumnosCursos",
            {
              idCurso: asignacion.idCurso,
              division: asignacion.division,
            }
          );

          const alumnosDivision = resp.data || [];

          alumnosDivision.forEach((al) => {
            mapaAlumnosPorMateria[String(bloque.idMateria)][
              String(al.alumnoDni)
            ] = {
              ...al,
              asignacion,
              materia,
            };
          });
        }
      }

      const resultado = [];

      filasExcel.forEach((fila) => {
        bloques.forEach((bloque) => {
          const materia = materiasPorId[String(bloque.idMateria)];
          const encontrado =
            mapaAlumnosPorMateria[String(bloque.idMateria)]?.[String(fila.dni)];

          const calculo = calcularNotaBloque(fila, bloque);

          if (!encontrado) {
            resultado.push({
              dni: fila.dni,
              bloque,
              materia,
              nota: calculo.nota,
              correctas: calculo.correctas,
              encontrado: false,
              alumno: null,
              asignacion: null,
              payload: null,
            });
            return;
          }

          resultado.push({
            dni: fila.dni,
            bloque,
            materia,
            nota: calculo.nota,
            correctas: calculo.correctas,
            encontrado: true,
            alumno: encontrado.alumno,
            asignacion: encontrado.asignacion,
            payload: {
              idAsignacionMateria: encontrado.asignacion.id,
              alumnoDni: fila.dni,
              idInstancia: instancia.id,
              nota: calculo.nota,
              estado: "Activo",
              califico:
                "SIGEDI_MOODLE:" +
                Usuario.identificacion +
                ` | Bloque ${bloque.numero} P.${bloque.desde} a P.${bloque.hasta} | Correctas ${calculo.correctas}/5`,
            },
          });
        });
      });

      setResultadoCruce(resultado);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo cruzar alumnos con divisiones.", "error");
    } finally {
      setCargando(false);
    }
  };

  const enviarNotas = async () => {
    const datos = resultadoCruce
      .filter((r) => r.encontrado && r.payload)
      .map((r) => r.payload);

    if (!datos.length) {
      Swal.fire("Atención", "No hay notas válidas para enviar.", "warning");
      return;
    }

    const noEncontrados = resultadoCruce.filter((r) => !r.encontrado).length;

    const confirm = await Swal.fire({
      title: "Confirmar importación",
      html: `
        <p>Se enviarán <b>${datos.length}</b> notas.</p>
        ${
          noEncontrados
            ? `<p style="color:#b91c1c;">Hay ${noEncontrados} cruces no encontrados y no se enviarán.</p>`
            : ""
        }
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, enviar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    try {
      setCargando(true);
// console.log(datos)
      await axios.post(
        "https://wwwa.isepsantafe.net.ar/expedientes/calificaciones/enviaNotas/",
        { datos }
      );

      Swal.fire("Éxito", "Notas importadas correctamente.", "success");

      if (cierro) cierro();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudieron enviar las notas.", "error");
    } finally {
      setCargando(false);
    }
  };

  const resumen = useMemo(() => {
    const total = resultadoCruce.length;
    const encontrados = resultadoCruce.filter((r) => r.encontrado).length;
    const noEncontrados = resultadoCruce.filter((r) => !r.encontrado).length;
    return { total, encontrados, noEncontrados };
  }, [resultadoCruce]);

  if (!instancia) {
    return (
      <div style={styles.contenedor}>
        <div style={styles.alerta}>No se recibió instancia.</div>
      </div>
    );
  }

  return (
    <div style={styles.contenedor}>
      <h3>Importar notas Moodle por bloques de 5 preguntas</h3>

      <div style={styles.card}>
        <p>
          <b>Curso:</b> {instancia?.nombreCurso || "-"}
        </p>
        <p>
          <b>Instancia:</b> {instancia?.nombreInstancia || instancia?.id || "-"}
        </p>

        <button onClick={cargarMaterias} style={styles.boton} disabled={cargando}>
          {cargando ? "Cargando..." : "Cargar materias del curso"}
        </button>
      </div>

      <div style={styles.card}>
        <label style={styles.label}>Excel de Moodle</label>

        <input type="file" accept=".xlsx,.xls" onChange={leerExcel} />

        {archivoNombre && (
          <p>
            Archivo: <b>{archivoNombre}</b>
          </p>
        )}

        {filasExcel.length > 0 && (
          <p>
            Alumnos detectados: <b>{filasExcel.length}</b>
          </p>
        )}
      </div>

      {bloques.length > 0 && materias.length > 0 && (
        <div style={styles.card}>
          <h4>Asociar bloques de preguntas con materias</h4>

          <div style={styles.bloquesGrid}>
            {bloques.map((bloque) => (
              <div key={bloque.numero} style={styles.bloqueBox}>
                <label style={styles.label}>
                  Bloque {bloque.numero} — Preguntas {bloque.desde} a{" "}
                  {bloque.hasta}
                </label>

                <select
                  value={bloque.idMateria}
                  onChange={(e) =>
                    cambiarMateriaBloque(bloque.numero, e.target.value)
                  }
                  style={styles.select}
                >
                  <option value="">Seleccionar materia</option>

                  {materias.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.nombreMateria}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <button
            onClick={cruzarDatos}
            style={styles.botonVerde}
            disabled={cargando}
          >
            Cruzar y preparar notas
          </button>
        </div>
      )}

      {resultadoCruce.length > 0 && (
        <>
          <div style={styles.resumen}>
            <div style={styles.resumenCard}>
              <span>Total notas</span>
              <b>{resumen.total}</b>
            </div>

            <div style={styles.resumenCard}>
              <span>Encontradas</span>
              <b style={{ color: "#15803d" }}>{resumen.encontrados}</b>
            </div>

            <div style={styles.resumenCard}>
              <span>No encontradas</span>
              <b style={{ color: "#b91c1c" }}>{resumen.noEncontrados}</b>
            </div>
          </div>

          <div style={styles.tablaWrapper}>
            <table style={styles.tabla}>
              <thead>
                <tr>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>DNI</th>
                  <th style={styles.th}>Alumno</th>
                  <th style={styles.th}>Bloque</th>
                  <th style={styles.th}>Materia</th>
                  <th style={styles.th}>Correctas</th>
                  <th style={styles.th}>Nota</th>
                  <th style={styles.th}>División</th>
                  <th style={styles.th}>Sede</th>
                  <th style={styles.th}>idAsignacionMateria</th>
                  <th style={styles.th}>Estado</th>
                </tr>
              </thead>

              <tbody>
                {resultadoCruce.map((r, index) => (
                  <tr
                    key={index}
                    style={{
                      background: r.encontrado ? "#dcfce7" : "#fee2e2",
                    }}
                  >
                    <td style={styles.td}>{index + 1}</td>
                    <td style={styles.td}>{r.dni}</td>
                    <td style={styles.td}>
                      {r.encontrado
                        ? `${r.alumno?.apellido || ""} ${
                            r.alumno?.nombres || ""
                          }`
                        : "-"}
                    </td>
                    <td style={styles.td}>
                      {r.bloque?.numero} <br />
                      P.{r.bloque?.desde} a P.{r.bloque?.hasta}
                    </td>
                    <td style={styles.td}>{r.materia?.nombreMateria || "-"}</td>
                    <td style={styles.td}>{r.correctas}/5</td>
                    <td style={styles.td}>
                      <b>{r.nota}</b>
                    </td>
                    <td style={styles.td}>{r.asignacion?.division || "-"}</td>
                    <td style={styles.td}>{r.asignacion?.sedeCursada || "-"}</td>
                    <td style={styles.td}>{r.asignacion?.id || "-"}</td>
                    <td style={styles.td}>
                      {r.encontrado ? "LISTO" : "DNI NO ENCONTRADO"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ textAlign: "right", marginTop: 15 }}>
            <button onClick={enviarNotas} style={styles.boton} disabled={cargando}>
              Enviar notas a SIGEDI
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  contenedor: {
    padding: 20,
    fontFamily: "Arial, sans-serif",
  },
  alerta: {
    background: "#fee2e2",
    color: "#991b1b",
    padding: 15,
    borderRadius: 10,
    border: "1px solid #fecaca",
  },
  card: {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 15,
    marginBottom: 15,
  },
  label: {
    display: "block",
    fontWeight: "bold",
    marginBottom: 6,
  },
  select: {
    width: "100%",
    padding: 10,
    borderRadius: 8,
    border: "1px solid #d1d5db",
  },
  boton: {
    background: "#2563eb",
    color: "white",
    border: "none",
    padding: "10px 15px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "bold",
  },
  botonVerde: {
    background: "#15803d",
    color: "white",
    border: "none",
    padding: "10px 15px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "bold",
    marginTop: 10,
  },
  bloquesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 12,
  },
  bloqueBox: {
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 12,
  },
  resumen: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 10,
    marginBottom: 15,
  },
  resumenCard: {
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 12,
    display: "flex",
    flexDirection: "column",
  },
  tablaWrapper: {
    overflowX: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
  },
  tabla: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 1400,
  },
  th: {
    background: "#1e293b",
    color: "white",
    padding: 10,
    border: "1px solid #334155",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  td: {
    padding: 10,
    border: "1px solid #e5e7eb",
    textAlign: "center",
    verticalAlign: "middle",
  },
};

export default ImportarNotasMoodleSIGEDI;